// Client-side file upload validation
import i18n from '../i18n';

export const FILE_LIMITS = {
  /** Max image size: 20 MB */
  MAX_IMAGE_SIZE: 20 * 1024 * 1024,
  /** Max audio size: 50 MB */
  MAX_AUDIO_SIZE: 50 * 1024 * 1024,
  /** Max video size: 100 MB */
  MAX_VIDEO_SIZE: 100 * 1024 * 1024,
  /** Max video duration: 3 minutes */
  MAX_VIDEO_DURATION_SEC: 180,
  /** Max audio duration: 7 minutes */
  MAX_AUDIO_DURATION_SEC: 420,
} as const;

export interface FileValidation {
  valid: boolean;
  error: string | null;
}

/** Allowed MIME types per media category */
const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  audio: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/x-m4a', 'audio/aac', 'audio/flac', 'audio/x-wav'],
  video: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'],
};

/** Allowed file extensions per media category (fallback when MIME is empty) */
const ALLOWED_EXTENSIONS: Record<string, string[]> = {
  image: ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
  audio: ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'],
  video: ['.mp4', '.webm', '.ogv', '.mov'],
};

/** Known dangerous extensions that should never be uploaded */
const BLOCKED_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
  '.sh', '.bash', '.ps1', '.vbs', '.js', '.ws', '.wsf',
  '.dll', '.sys', '.cpl', '.inf', '.reg',
  '.php', '.jsp', '.asp', '.aspx', '.py', '.rb', '.pl',
  '.html', '.htm', '.svg', '.xml',
  '.zip', '.rar', '.7z', '.tar', '.gz',
];

function getExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot).toLowerCase() : '';
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function getMaxSize(mediaType: string): number {
  switch (mediaType) {
    case 'image': return FILE_LIMITS.MAX_IMAGE_SIZE;
    case 'audio': return FILE_LIMITS.MAX_AUDIO_SIZE;
    case 'video': return FILE_LIMITS.MAX_VIDEO_SIZE;
    default: return FILE_LIMITS.MAX_IMAGE_SIZE;
  }
}

/** Validate a file before upload */
export function validateFile(file: File, mediaType: string): FileValidation {
  const ext = getExtension(file.name);

  // Block dangerous extensions
  if (BLOCKED_EXTENSIONS.includes(ext)) {
    return { valid: false, error: i18n.t('validation.fileTypeBlocked', { ext }) };
  }

  // Check MIME type
  const allowedMimes = ALLOWED_MIME_TYPES[mediaType];
  if (allowedMimes) {
    const mimeOk = file.type && allowedMimes.includes(file.type.toLowerCase());
    const extOk = ALLOWED_EXTENSIONS[mediaType]?.includes(ext);
    // Accept if EITHER mime or extension matches (browsers sometimes report wrong MIME)
    if (!mimeOk && !extOk) {
      return {
        valid: false,
        error: i18n.t('validation.fileTypeInvalid', { type: mediaType, accepted: ALLOWED_EXTENSIONS[mediaType]?.join(', ') }),
      };
    }
  }

  // Check file size
  const maxSize = getMaxSize(mediaType);
  if (file.size > maxSize) {
    return {
      valid: false,
      error: i18n.t('validation.fileTooLarge', { size: formatSize(file.size), type: mediaType, max: formatSize(maxSize) }),
    };
  }

  // Check empty file
  if (file.size === 0) {
    return { valid: false, error: i18n.t('validation.fileEmpty') };
  }

  return { valid: true, error: null };
}

/** Check media duration before upload. Returns duration in seconds, or throws with error message. */
export function checkMediaDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const isVideo = file.type.startsWith('video');
    const isAudio = file.type.startsWith('audio');
    if (!isVideo && !isAudio) {
      resolve(0);
      return;
    }
    const el = document.createElement(isVideo ? 'video' : 'audio');
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(el.src);
      const maxSec = isVideo ? FILE_LIMITS.MAX_VIDEO_DURATION_SEC : FILE_LIMITS.MAX_AUDIO_DURATION_SEC;
      const maxLabel = isVideo ? '3 min' : '7 min';
      if (el.duration > maxSec) {
        reject(new Error(
          i18n.t('validation.fileDurationExceeded', { duration: Math.round(el.duration), max: maxLabel })
        ));
      } else {
        resolve(Math.round(el.duration));
      }
    };
    el.onerror = () => resolve(0);
    el.src = URL.createObjectURL(file);
  });
}
